/*
 * Clients, their contacts and properties, notes and files (draft 2). Client information and job information are separate
 * records: one client can have several jobs, and nothing about a job is kept on the client.
 *
 * A client with a company name, or marked as a business without one, is a business (past due after 7 days instead of 24
 * hours, BUILD-SPEC 3.7).
 */
import { z } from "zod";
import { listQuery, phoneSchema } from "./api.zod.js";
import { addressParts } from "./address.js";

const dateString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, "Use a date like 2026-11-04.");
const text = (max: number) => z.string().trim().max(max);
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === "" ? null : s))
    .nullable()
    .optional();

export const INACTIVE_REASONS = ["unpaid", "complained", "cancelled", "moved", "elsewhere", "not_needed", "other"] as const;
export type InactiveReason = (typeof INACTIVE_REASONS)[number];

/** Why a client stopped, in the screens' words. */
export const INACTIVE_REASON_WORDS: Record<InactiveReason, string> = {
  unpaid: "Did not pay",
  complained: "Was not happy",
  cancelled: "Cancelled",
  moved: "Moved away",
  elsewhere: "Went with someone else",
  not_needed: "No longer needs it",
  other: "Other",
};

export const LEAD_SOURCES = [
  "google",
  "lsa",
  "facebook",
  "referral",
  "website",
  "yard_sign",
  "repeat",
  "jobber",
  "other",
] as const;

// Contacts ---------------------------------------------------------------------------------------------------------

/** What an extra phone or email is for (owner ask, 2026-11-02): office, cell, billing and so on. */
export const CONTACT_TYPES = ["cell", "office", "home", "work", "fax", "billing", "personal", "other"] as const;
export type ContactType = (typeof CONTACT_TYPES)[number];
export const CONTACT_TYPE_WORDS: Record<ContactType, string> = {
  cell: "Cell",
  office: "Office",
  home: "Home",
  work: "Work",
  fax: "Fax",
  billing: "Billing",
  personal: "Personal",
  other: "Other",
};
/** The types each kind offers, the usual one first. */
export const PHONE_TYPES: readonly ContactType[] = ["cell", "office", "home", "work", "fax", "other"];
export const EMAIL_TYPES: readonly ContactType[] = ["billing", "work", "personal", "office", "other"];

export const contactInput = z.object({
  kind: z.enum(["phone", "email"]),
  value: text(200).min(3, "Type the phone number or email."),
  type: z.enum(CONTACT_TYPES).nullable().optional(),
  /** Who it reaches, like "Mike" or "tenant". */
  label: optionalText(40),
  isMain: z.boolean().optional(),
});
export type ContactInput = z.infer<typeof contactInput>;

export const contactRow = z.object({
  id: z.uuid(),
  kind: z.enum(["phone", "email"]),
  value: z.string(),
  type: z.enum(CONTACT_TYPES).nullable(),
  label: z.string().nullable(),
  isMain: z.boolean(),
  /** Said STOP or no to marketing. */
  optedOut: z.string().nullable(),
});
export type ContactRow = z.infer<typeof contactRow>;

// Properties -------------------------------------------------------------------------------------------------------

export const propertyInput = z.object({
  label: optionalText(60),
  address: text(200).min(3, "Type the street address."),
  city: optionalText(80),
  state: optionalText(2),
  zip: optionalText(10),
  beds: z.number().int().min(0).max(50).nullable().optional(),
  baths: z.number().min(0).max(50).nullable().optional(),
  rooms: z.number().int().min(0).max(100).nullable().optional(),
  sqft: z.number().int().min(0).max(1_000_000).nullable().optional(),
  lawnSqft: z.number().int().min(0).max(10_000_000).nullable().optional(),
  /** Gate and alarm codes. Office only, kept encrypted. */
  accessNotes: optionalText(500),
  notes: optionalText(2000),
});
export type PropertyInput = z.infer<typeof propertyInput>;
export const propertyPatch = propertyInput.partial();

export const propertyRow = z.object({
  id: z.uuid(),
  label: z.string().nullable(),
  address: z.string(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  beds: z.number().nullable(),
  baths: z.number().nullable(),
  rooms: z.number().nullable(),
  sqft: z.number().nullable(),
  lawnSqft: z.number().nullable(),
  /** Office only; null for roles that may not see it. */
  accessNotes: z.string().nullable(),
  notes: z.string().nullable(),
  /** "2 bed, 1 bath, 1,200 sq ft" for the folded row. */
  summary: z.string(),
});
export type PropertyRow = z.infer<typeof propertyRow>;

// Notes and files ----------------------------------------------------------------------------------------------------

export const NOTE_VISIBILITY = ["office", "crew"] as const;
export const noteInput = z.object({
  body: text(5000).min(1, "Type the note."),
  visibility: z.enum(NOTE_VISIBILITY).default("office"),
  pinned: z.boolean().default(false),
});
export type NoteInput = z.infer<typeof noteInput>;
export const notePatch = z
  .object({ body: text(5000).min(1), visibility: z.enum(NOTE_VISIBILITY), pinned: z.boolean() })
  .partial();

export const noteRow = z.object({
  id: z.uuid(),
  body: z.string(),
  visibility: z.enum(NOTE_VISIBILITY),
  pinned: z.boolean(),
  authorName: z.string().nullable(),
  createdAt: z.string(),
});
export type NoteRow = z.infer<typeof noteRow>;

export const FILE_KINDS = ["photo", "pdf", "w9", "id", "insurance", "contract", "license", "other"] as const;
export const fileRow = z.object({
  id: z.uuid(),
  name: z.string(),
  mime: z.string(),
  size: z.number(),
  kind: z.enum(FILE_KINDS),
  createdAt: z.string(),
  /** A visit photo's yard (yard-spots.ts) or cleaning room. */
  room: z.string().nullable().optional(),
});
export type FileRow = z.infer<typeof fileRow>;
/** A small file sent as base64 in JSON (photos from a phone, a W-9). Big uploads come with real storage later. */
export const fileUpload = z.object({
  name: text(200).min(1),
  mime: z.string().regex(/^[\w.+-]+\/[\w.+-]+$/, "That is not a file type."),
  kind: z.enum(FILE_KINDS).default("other"),
  dataBase64: z.string().max(14_000_000, "That file is too big. The most is 10 MB."),
  /** For a visit photo: the room it shows (cleaning) or the yard (lawn, yard-spots.ts). */
  room: optionalText(40),
});

// Clients ---------------------------------------------------------------------------------------------------------

/** Jobber's Title choice on a new client. */
export const CLIENT_TITLES = ["Mr.", "Ms.", "Mrs.", "Miss", "Dr."] as const;

const clientFields = {
  title: z.enum(CLIENT_TITLES).nullable().optional(),
  firstName: optionalText(60),
  lastName: optionalText(60),
  /** Set for a business. A client with a company name is a business. */
  companyName: optionalText(120),
  /** A business with no company name to show (a landlord, a church run by one person). A company name makes it true. */
  isCompany: z.boolean().optional(),
  phone: phoneSchema.nullable().optional(),
  email: z.email("That email does not look right.").trim().toLowerCase().nullable().optional(),
  tags: z.array(text(40).min(1)).max(30).optional(),
  leadSource: optionalText(40),
  /** How they like to hear from us. */
  prefers: z.enum(["text", "email"]).optional(),
  noMarketing: z.boolean().optional(),
  notes: optionalText(5000),
  officeNotes: optionalText(5000),
  /** Where invoices go when it is not the property (Jobber's billing address), one line. Older callers; the form sends billing. */
  billingAddress: optionalText(300),
  /** The billing address in parts (owner rule, 2026-11-02). Null clears it. Wins over billingAddress when both come. */
  billing: addressParts.nullable().optional(),
  /** The client who sent them to us, picked by hand (a referral link fills it by itself). */
  referredById: z.uuid().nullable().optional(),
};

/**
 * A new client (owner rule, 2026-10-28; email optional since 2026-10-31): first name, phone and address are a must; last
 * name and email are asked for but may be left out. A business also gives a contact first name. Edits (clientPatch) and the WordPress copy keep the old, looser
 * shape so older clients with gaps still save.
 */
export const clientInput = z.object({
  ...clientFields,
  firstName: text(60).min(1, "Type their first name."),
  phone: phoneSchema,
  /** A blank email box is no email. */
  email: z.preprocess(
    (v) => (typeof v === "string" && !v.trim() ? null : v),
    z.email("That email does not look right.").trim().toLowerCase().nullable().optional(),
  ),
  /** The first property: where the work is. */
  property: propertyInput,
  /** More phones and emails beyond the main ones. */
  contacts: z.array(contactInput).max(20).optional(),
});
export type ClientInput = z.infer<typeof clientInput>;

/** One extra phone or email on the Edit client form: with an id it is changed, without one it is added. */
export const contactEdit = contactInput.extend({ id: z.uuid().optional() });
export type ContactEdit = z.infer<typeof contactEdit>;

export const clientPatch = z
  .object({
    ...clientFields,
    /** The whole list of extra phones and emails: ones left out are removed (main ones stay). */
    contacts: z.array(contactEdit).max(20),
  })
  .partial();
export type ClientPatch = z.infer<typeof clientPatch>;

export const clientRow = z.object({
  id: z.uuid(),
  name: z.string(),
  isCompany: z.boolean(),
  companyName: z.string().nullable(),
  phone: z.string().nullable(),
  email: z.string().nullable(),
  /** The main property's address, one line. */
  address: z.string().nullable(),
  tags: z.array(z.string()),
  leadSource: z.string().nullable(),
  active: z.boolean(),
  inactiveReason: z.enum(INACTIVE_REASONS).nullable(),
  mergedIntoId: z.uuid().nullable(),
  activeJobs: z.number(),
  lastVisit: z.string().nullable(),
  nextVisit: z.string().nullable(),
  /** The service of their newest job (an active one first), and its kind of work (service-category.ts). */
  service: z.string().nullable(),
  category: z.string().nullable(),
  createdAt: z.string(),
  /** Has an active job that repeats. */
  onPlan: z.boolean(),
  /** What they paid us for work, tips left out, refunds taken off. */
  lifetimeCents: z.number(),
  /** The newest thing that happened with them (client-health.ts), or null when nothing has yet. */
  lastActivity: z.object({ what: z.string(), at: z.string() }).nullable(),
  /** Why they may leave, in one line (client-health.ts riskWords), or null. */
  atRisk: z.string().nullable(),
});
export type ClientRow = z.infer<typeof clientRow>;

/** The client page. Jobs and visits come from their own routes (GET jobs?clientId=, GET visits?clientId=). */
export const clientDetail = clientRow.extend({
  title: z.string().nullable().optional(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  prefers: z.enum(["text", "email"]),
  noMarketing: z.boolean(),
  notes: z.string().nullable(),
  officeNotes: z.string().nullable(),
  inactiveNote: z.string().nullable(),
  inactiveAt: z.string().nullable(),
  contacts: z.array(contactRow),
  properties: z.array(propertyRow),
  notesList: z.array(noteRow),
  files: z.array(fileRow),
  /** Business: past due after 7 days. Home: after 24 hours. */
  kind: z.enum(["home", "business"]),
  /** The saved card (Payment methods), and whether it is charged by itself when a visit is done. */
  card: z
    .object({
      brand: z.string(),
      last4: z.string(),
      expMonth: z.number(),
      expYear: z.number(),
      /** The office typed it in while the client read it out (Take card by phone). */
      byPhone: z.boolean().optional(),
    })
    .nullable(),
  autoCharge: z.boolean(),
  /** A quote the client signed that is not a job yet: a job made for them by hand needs no confirm link. */
  signedQuote: z.boolean().optional(),
  /** A home cleaned (standard, deep or move) in the last 30 days: a plan made for them starts at the plan price. */
  planStart: z.boolean().optional(),
  /** "Home default (due in 24 hours)": when their invoices turn past due, from the brand's invoice settings. */
  paymentTerms: z.string().optional(),
  /** Who sent them to us: picked by hand, or the client whose referral link they came in through. */
  referredBy: z.object({ id: z.uuid(), name: z.string() }).nullable().optional(),
  billingAddress: z.string().nullable().optional(),
  /** The billing address in parts for the form; an old one line address is split as well as it can be. */
  billing: z
    .object({
      street1: z.string().nullable(),
      street2: z.string().nullable(),
      city: z.string().nullable(),
      state: z.string().nullable(),
      zip: z.string().nullable(),
    })
    .nullable()
    .optional(),
  /**
   * Their Google review, when one is matched to them (reviews.clientId), or the old Steward said they reviewed (no stars or
   * day then). Null when we know of none.
   */
  googleReview: z.object({ stars: z.number().int().nullable(), at: z.string().nullable() }).nullable().optional(),
});
export type ClientDetail = z.infer<typeof clientDetail>;

/** Recent pricing (Jobber's card): each service's last quoted price and its price on a job, newest first. */
export const pricingRow = z.object({
  description: z.string(),
  quotedCents: z.number().nullable(),
  quotedOn: z.string().nullable(),
  quoteId: z.uuid().nullable(),
  jobCents: z.number().nullable(),
  jobOn: z.string().nullable(),
  jobId: z.uuid().nullable(),
  jobNumber: z.number().nullable(),
});
export type PricingRow = z.infer<typeof pricingRow>;

/**
 * A reminder on the Client schedule: one the office set (done with the round check), or one the Steward sees for itself
 * (a signed quote with no job yet, an invoice past due), which goes away once that work is done.
 */
export const reminderRow = z.object({
  key: z.string(),
  kind: z.enum(["task", "quote", "invoice"]),
  /** The office's own reminder, or null for one the Steward made. */
  id: z.uuid().nullable(),
  dueOn: z.string(),
  dueTime: z.string().nullable(),
  title: z.string(),
  sub: z.string().nullable(),
  assignedName: z.string().nullable(),
  done: z.boolean(),
  quoteId: z.uuid().nullable(),
  invoiceId: z.uuid().nullable(),
});
export type ReminderRow = z.infer<typeof reminderRow>;

export const reminderInput = z
  .object({
    title: text(200).min(1, "Say what to remember."),
    note: optionalText(2000),
    dueOn: dateString,
    dueTime: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .nullable()
      .optional(),
    assignedTo: z.uuid().nullable().optional(),
  })
  .strict();
export type ReminderInput = z.infer<typeof reminderInput>;
export const reminderPatch = z.object({ done: z.boolean() }).strict();
export const threadStart = z.object({ channel: z.enum(["sms", "email"]) }).strict();

export const clientListQuery = listQuery.extend({
  q: z.string().trim().max(100).optional(),
  status: z.enum(["active", "inactive", "all"]).default("active"),
  tag: z.string().trim().max(40).optional(),
  sort: z.enum(["name", "newest", "next_visit", "last_activity", "lifetime"]).default("name"),
  /** Each sort has its own usual way (A to Z, newest first, biggest first); this turns it around. */
  dir: z.enum(["asc", "desc"]).optional(),
  /** The tiles' filters: clients on a plan, active ones with nothing booked, or ones at risk. */
  show: z.enum(["plan", "nothing_booked", "at_risk"]).optional(),
});
export type ClientListQuery = z.infer<typeof clientListQuery>;

export const clientSummary = z.object({
  active: z.number(),
  inactive: z.number(),
  newThisMonth: z.number(),
  withoutJobs: z.number(),
  /** New leads (Pipeline) and new clients, the past 30 days and the 30 before, and new clients since Jan 1. */
  leads30: z.number(),
  leadsBefore30: z.number(),
  new30: z.number(),
  newBefore30: z.number(),
  newThisYear: z.number(),
  /** Active clients with a repeating job. */
  onPlan: z.number(),
  /** Active clients riskWords flags. */
  atRisk: z.number(),
  tags: z.array(z.object({ tag: z.string(), count: z.number() })),
});
export type ClientSummary = z.infer<typeof clientSummary>;

export const deactivateBody = z.object({ reason: z.enum(INACTIVE_REASONS), note: optionalText(500) });

/** Merge this client (the duplicate) into another (the one kept). */
export const mergeBody = z.object({ into: z.uuid() });
export const mergeAnswer = z.object({
  kept: z.uuid(),
  closed: z.uuid(),
  moved: z.object({
    jobs: z.number(),
    visits: z.number(),
    properties: z.number(),
    contacts: z.number(),
    notes: z.number(),
    files: z.number(),
  }),
});
export type MergeAnswer = z.infer<typeof mergeAnswer>;

/**
 * Many clients at once. More than 20 needs the owner and the confirm token the first try answered with (the fail-safe,
 * BUILD-SPEC 3.4: the old system once lost its client list).
 */
export const bulkClientsBody = z.object({
  ids: z.array(z.uuid()).min(1).max(500),
  action: z.enum(["deactivate", "reactivate", "tag", "untag"]),
  reason: z.enum(INACTIVE_REASONS).optional(),
  tag: text(40).min(1).optional(),
  confirm: z.string().max(100).optional(),
});
export type BulkClientsBody = z.infer<typeof bulkClientsBody>;
export const bulkAnswer = z.object({ done: z.number(), ids: z.array(z.uuid()) });
export type BulkAnswer = z.infer<typeof bulkAnswer>;
export const BULK_LIMIT = 20;

/** The one-line summary of a property for its folded row: "2 bed, 1 bath, 1,200 sq ft". */
export function propertySummary(p: {
  beds?: number | null;
  baths?: number | null;
  rooms?: number | null;
  sqft?: number | null;
  lawnSqft?: number | null;
}): string {
  const parts: string[] = [];
  if (p.beds != null) parts.push(`${String(p.beds)} bed`);
  if (p.baths != null) parts.push(`${String(p.baths)} bath`);
  // A home's rooms are the ones past its bedrooms and bathrooms; a business has only rooms.
  if (p.rooms != null)
    parts.push(
      p.beds != null || p.baths != null
        ? `${String(p.rooms)} other ${p.rooms === 1 ? "room" : "rooms"}`
        : `${String(p.rooms)} rooms`,
    );
  if (p.sqft != null) parts.push(`${p.sqft.toLocaleString("en-US")} sq ft`);
  if (p.lawnSqft != null) parts.push(`${p.lawnSqft.toLocaleString("en-US")} sq ft lawn`);
  return parts.length ? parts.join(", ") : "No size yet";
}

/** How a client shows in lists: the company name for a business, else first and last name. */
export function clientName(c: {
  firstName?: string | null | undefined;
  lastName?: string | null | undefined;
  companyName?: string | null | undefined;
}): string {
  const person = [c.firstName, c.lastName]
    .filter((s): s is string => Boolean(s?.trim()))
    .join(" ")
    .trim();
  const company = c.companyName?.trim() ?? "";
  if (company !== "") return company;
  return person === "" ? "Client" : person;
}

export { dateString };
